import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AgencyMembership } from '../../api/agencies.api';
import type { AppMode } from '../../stores/agencies.store';
import { Building2, Check, ShieldCheck, User, Icon, type LucideIcon } from '../ui/Icon';

interface Props {
  /** The agency staff switch to; null for an account that works at none. */
  agency: AgencyMembership | null;
  modes: AppMode[];
  mode: AppMode;
  onMode: (mode: AppMode) => void;
  onDone: () => void;
}

/**
 * ADR-0036: one account, several experiences — its own trips, its agency's,
 * and Gromly's administration. Only the ones the account has are listed; the
 * server still decides trip by trip and call by call.
 */
export default function ModeSwitch({ agency, modes, mode, onMode, onDone }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const option = (m: AppMode): { label: string; icon: LucideIcon; logoUrl?: string | null } => {
    switch (m) {
      case 'personal': return { label: t('nav.mode.personal'), icon: User };
      case 'agency': return { label: agency?.name ?? '', icon: Building2, logoUrl: agency?.logoUrl };
      case 'admin': return { label: t('nav.mode.admin'), icon: ShieldCheck };
    }
  };

  function choose(next: AppMode) {
    onDone();
    if (next === mode) return;
    onMode(next);
    navigate('/dashboard');
  }

  return (
    <div className="py-1 border-b border-slate-100 dark:border-slate-800" role="group" aria-label={t('nav.mode.label')}>
      <p className="px-3 pt-1.5 pb-1 font-mono text-[9px] tracking-[0.14em] uppercase text-slate-400">{t('nav.mode.label')}</p>
      {modes.map(m => {
        const o = option(m);
        return (
          <button key={m} type="button" onClick={() => choose(m)} aria-pressed={m === mode}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors cursor-pointer ${m === mode
              ? 'text-teal-700 dark:text-teal-300 font-medium'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
            {o.logoUrl
              ? <img src={o.logoUrl} alt="" className="w-[18px] h-[18px] rounded object-contain bg-white shrink-0" />
              : <Icon as={o.icon} size={14} className="shrink-0" />}
            <span className="truncate flex-1 text-left">{o.label}</span>
            {m === mode && <Icon as={Check} size={14} className="shrink-0" />}
          </button>
        );
      })}
    </div>
  );
}
